import { useState, useEffect, useCallback } from "react";
import { Plus, ShoppingCart, ShoppingBag, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/formatters";
import { CustomerModal } from "./modals/CustomerModal";
import { VendorModal } from "./modals/VendorModal";
import { useCustomers, useVendors } from "@/hooks/useInventoryQueries";
import { useWindowManager } from "@/contexts/WindowManagerContext";

type InvoiceType = 'sale' | 'purchase' | null;
type Party = {
  id: string;
  name: string;
  phone?: string;
  current_balance?: number;
  company_name?: string;
};

export const FloatingInvoiceButton = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPartySelectOpen, setIsPartySelectOpen] = useState(false);
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<InvoiceType>(null);
  const [selectedPartyId, setSelectedPartyId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [isCreatePartyOpen, setIsCreatePartyOpen] = useState(false);
  
  const { openWindow, windows, bringToFront } = useWindowManager();
  // Use React Query for cached data
  const { data: customers = [], refetch: refetchCustomers } = useCustomers();
  const { data: vendors = [], refetch: refetchVendors } = useVendors();

  // Get parties based on selected invoice type
  const parties = selectedInvoiceType === 'sale' ? customers : vendors;

  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async (): Promise<string | null> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: org } = await supabase
      .from('organizations')
      .select('id')
      .eq('owner_id', user.id)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (org) {
      setOrganizationId(org.id);
      return org.id;
    }
    return null;
  };

  const handleMainButtonClick = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInvoiceTypeSelect = async (type: 'sale' | 'purchase') => {
    setSelectedInvoiceType(type);
    setIsExpanded(false);
    // Data is already cached, just refetch to ensure freshness
    if (type === 'sale') {
      refetchCustomers();
    } else {
      refetchVendors();
    }
    setIsPartySelectOpen(true);
    setSearchTerm("");
  };

  const handlePartySelect = useCallback((partyId: string) => {
    setSelectedPartyId(partyId);
    setIsPartySelectOpen(false);
    
    // Check if an invoice window already exists for this party/type
    const windowType = 'invoice';
    const existingWindow = windows.find(w => 
      w.type === windowType && 
      ((selectedInvoiceType === 'sale' && w.props.customerId === partyId) ||
       (selectedInvoiceType === 'purchase' && w.props.vendorId === partyId))
    );
    
    if (existingWindow) {
      bringToFront(existingWindow.id);
      return;
    }
    
    // Open invoice via WindowManager
    openWindow('invoice', {
      invoiceType: selectedInvoiceType,
      customerId: selectedInvoiceType === 'sale' ? partyId : undefined,
      vendorId: selectedInvoiceType === 'purchase' ? partyId : undefined,
      onSuccess: () => {
        setSelectedPartyId(null);
        setSelectedInvoiceType(null);
      },
    }, selectedInvoiceType === 'sale' ? 'Create Sales Invoice' : 'Create Purchase Invoice');
  }, [selectedInvoiceType, windows, bringToFront, openWindow]);

  const handleCreateNewParty = async () => {
    let orgId = organizationId;
    if (!orgId) {
      orgId = await fetchOrganization();
    }
    if (!orgId) {
      toast.error("No organization found. Please set up your business first.");
      return;
    }
    setIsPartySelectOpen(false);
    setIsCreatePartyOpen(true);
  };

  const handlePartyCreated = () => {
    setIsCreatePartyOpen(false);
    if (selectedInvoiceType === 'sale') {
      refetchCustomers();
    } else if (selectedInvoiceType === 'purchase') {
      refetchVendors();
    }
    setIsPartySelectOpen(true);
  };

  const filteredParties = parties.filter(party => {
    const matchesName = party.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPhone = party.phone && party.phone.includes(searchTerm);
    // Only vendors have company_name
    const matchesCompany = selectedInvoiceType === 'purchase' && 
      'company_name' in party && 
      typeof party.company_name === 'string' &&
      party.company_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesName || matchesPhone || matchesCompany;
  });

  return (
    <>
      {/* Main FAB */}
      <Button
        onClick={handleMainButtonClick}
        className={`fixed bottom-16 right-20 h-12 w-12 rounded-full shadow-glow hover:shadow-lg transition-all duration-300 z-20 financial-button ${
          isExpanded ? 'rotate-45' : ''
        }`}
        size="icon"
        aria-label="Create invoice"
      >
        <Plus className="h-5 w-5" />
      </Button>

      {/* Expandable Options */}
      {isExpanded && (
        <>
          {/* Selling Invoice Button */}
          <Button
            onClick={() => handleInvoiceTypeSelect('sale')}
            className="fixed bottom-32 right-20 h-12 px-4 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg transition-all duration-300 z-20 animate-slide-in-up"
            style={{ animationDelay: '0ms' }}
            aria-label="Create selling invoice"
          >
            <ShoppingCart className="h-5 w-5 mr-2" />
            <span className="text-sm font-medium">Selling Invoice</span>
          </Button>

          {/* Buying Invoice Button */}
          <Button
            onClick={() => handleInvoiceTypeSelect('purchase')}
            className="fixed bottom-48 right-20 h-12 px-4 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg transition-all duration-300 z-20 animate-slide-in-up"
            style={{ animationDelay: '100ms' }}
            aria-label="Create buying invoice"
          >
            <ShoppingBag className="h-5 w-5 mr-2" />
            <span className="text-sm font-medium">Buying Invoice</span>
          </Button>
        </>
      )}

      {/* Party Selection Dialog */}
      <Dialog open={isPartySelectOpen} onOpenChange={setIsPartySelectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Select {selectedInvoiceType === 'sale' ? 'Customer' : 'Vendor'} for{' '}
              {selectedInvoiceType === 'sale' ? 'Sales' : 'Purchase'} Invoice
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, phone, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Party List */}
            <ScrollArea className="h-[300px] pr-4">
              {filteredParties.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No {selectedInvoiceType === 'sale' ? 'customers' : 'vendors'} found
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredParties.map((party) => (
                    <Card
                      key={party.id}
                      onClick={() => handlePartySelect(party.id)}
                      className="cursor-pointer hover:bg-accent transition-colors"
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="space-y-1">
                            <p className="font-semibold">{party.name}</p>
                            {'company_name' in party && typeof party.company_name === 'string' && party.company_name && (
                              <p className="text-sm text-muted-foreground">{party.company_name}</p>
                            )}
                            {party.phone && (
                              <p className="text-sm text-muted-foreground">{party.phone}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Balance</p>
                            <p
                              className={`text-sm font-semibold ${
                                (party.current_balance || 0) > 0
                                  ? 'text-red-600'
                                  : (party.current_balance || 0) < 0
                                  ? 'text-green-600'
                                  : 'text-muted-foreground'
                              }`}
                            >
                              {formatCurrency(Math.abs(party.current_balance || 0))}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>

            {/* Create New Button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={handleCreateNewParty}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create New {selectedInvoiceType === 'sale' ? 'Customer' : 'Vendor'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Modal is now rendered via WindowRenderer */}

      {/* Create Party Modals */}
      {organizationId && selectedInvoiceType === 'sale' && (
        <CustomerModal
          open={isCreatePartyOpen}
          onOpenChange={setIsCreatePartyOpen}
          organizationId={organizationId}
          onSuccess={handlePartyCreated}
        />
      )}

      {organizationId && selectedInvoiceType === 'purchase' && (
        <VendorModal
          open={isCreatePartyOpen}
          onOpenChange={setIsCreatePartyOpen}
          organizationId={organizationId}
          onSuccess={handlePartyCreated}
        />
      )}
    </>
  );
};
